import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { MailCheck } from "lucide-react";
import { toast } from "sonner";

export default function VerifyEmail() {
  const { user, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const email = params.get("email") || user?.email || "";
  const nextParam = params.get("next");
  const next = nextParam && nextParam.startsWith("/") ? nextParam : null;
  const [code, setCode] = useState("");
  const [loading, setLoading] = useState(false);

  const verify = async (e) => {
    e.preventDefault();
    if (!code.trim()) return;
    setLoading(true);
    try {
      await api.post("/auth/verify-email", { email, code: code.trim() });
      toast.success("Email verified! You're all set.");
      try { const me = await api.get("/auth/me"); refreshUser(me.data.user); } catch (err) { console.error("Failed to refresh user after email verify", err); }
      if (next) navigate(next);
      else if (user?.role === "subscriber") navigate("/dashboard");
      else if (user?.customer_type === "business") navigate("/business");
      else navigate("/browse");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  const resend = async () => {
    try { await api.post("/auth/resend-verification", { email }); toast.success("New code sent — check your inbox."); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  return (
    <div className="max-w-md mx-auto px-4 sm:px-6 py-16">
      <div className="w-12 h-12 rounded-sm bg-primary/10 flex items-center justify-center mb-5">
        <MailCheck className="w-6 h-6 text-primary" />
      </div>
      <h1 className="font-display text-3xl font-semibold tracking-tight">Verify your email</h1>
      <p className="text-muted-foreground mt-2">
        We sent a 6-digit code to <b className="text-foreground" data-testid="verify-email-addr">{email || "your email"}</b>. Enter it below to activate your account.
      </p>
      <form onSubmit={verify} className="mt-8 space-y-4">
        <Input
          value={code}
          onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
          placeholder="Enter 6-digit code"
          inputMode="numeric"
          className="rounded-sm text-center tracking-[0.4em] text-lg"
          data-testid="verify-code-input"
        />
        <Button type="submit" disabled={loading || code.length < 6} className="w-full rounded-sm" data-testid="verify-submit">
          {loading ? "Verifying…" : "Verify email"}
        </Button>
      </form>
      <div className="mt-5 text-sm text-muted-foreground flex items-center justify-between">
        <button onClick={resend} className="text-primary font-medium hover:underline" data-testid="verify-resend">Resend code</button>
        <Link to="/browse" className="hover:text-foreground">Skip for now</Link>
      </div>
      <p className="text-xs text-muted-foreground mt-6">
        You must verify your email before you can book or pay for services.
      </p>
    </div>
  );
}
